import { useMutation, useQueryClient } from '@tanstack/react-query';
import { NavigateFunction } from 'react-router-dom';

import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import {
  AlertSeverityEnum,
  EstadoActivacionCodigoEnum,
  ObservacionesPreventa,
  UseMutationsParams,
} from '@/shared/interfaces';
import {
  FotosRechazoTecnico,
  ObservacionesTecnicoType,
  OrdenTrabajoOld,
  TecnicoOrdenTrabajoForm,
} from '@/shared/interfaces/erp/tecnico';
import { useUiStore } from '@/store/ui';

const { put } = erpApi();

///* tanSctack query
export const useSaveOrdenTrabajoTecnicoAsignada = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: saveOTByTecnico,
    onSuccess: res => {
      queryClient.invalidateQueries({ queryKey: ['ordenesTrabajo'] });

      res.status === 'success' &&
        setAlert(true, 'Orden de trabajo guardada correctamente', 'success');

      return navigate(returnUrl, { replace: true });
    },
  });
};

export const useRejectOrdenTrabajoTecnico = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: rejectOTByTecnico,
    onSuccess: res => {
      queryClient.invalidateQueries({ queryKey: ['ordenesTrabajo'] });

      res.status === 'success' &&
        setAlert(true, 'Orden de trabajo rechazada correctamente', 'success');

      return navigate(returnUrl, { replace: true });
    },
  });
};

export const useUpdateOrdenTrabajoGeneric = ({
  navigate,
  returnUrl,
  customMessage,
  enableAlert = false,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updOTGeneric,
    onSuccess: res => {
      queryClient.invalidateQueries({
        queryKey: ['ordeneTrabajo', 'ordenesTrabajo'],
      });

      if (res.status === 'success') {
        enableAlert &&
          setAlert(
            true,
            customMessage ?? 'Actualizado correctamente',
            AlertSeverityEnum.success
          );

        navigate && returnUrl && navigate(returnUrl, { replace: true });
      }
    },
  });
};

///* axios
export type UpdOTSaveByTecnicoParams = {
  id: string;
  data: TecnicoOrdenTrabajoForm;
};

type UpdOTGenericData = Partial<OrdenTrabajoOld> & {
  odenTrabajoIdUpd?: number;
  servicioInternetIdUpd?: number;
};
export type UpdOTGeneric = {
  id: string;
  data: UpdOTGenericData;
};

export const saveOTByTecnico = async ({
  data,
}: UpdOTSaveByTecnicoParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put<OrdenTrabajoOld>(
    `${urlBackend}api/v1/ordenes-trabajo/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const updOTGeneric = async ({ id, data }: UpdOTGeneric) => {
  return put<OrdenTrabajoOld>(
    `${urlBackend}api/v1/ordenes-trabajo/${id}/`,
    data,
    true
  );
};

export type UpdOTRejectByTecnicoParams = {
  id: string;
  data: RejectOTByTecnicoData;
};
export type RejectOTByTecnicoData = {
  motivo_rechazo?: string;
  estado_orden_trabajo?: string;
  recoordination_required?: boolean;
  tipo_novedad?: string;
  estado_imagen_actualizacion?: string;
  observaciones?: ObservacionesTecnicoType;
  urls_fotos_rechazo_tecnico?: FotosRechazoTecnico[];
};

export type RejectPreventaData = {
  motivo_rechazo?: string;
  estado_preventa?: string;
  // recoordination_required?: boolean;
  // estado_imagen_actualizacion?: string;
  observaciones?: ObservacionesPreventa;
  // urls_fotos_rechazo_tecnico?: FotosRechazoTecnico[];
};

export type RejectOTByTecnicoDataAuditoria = RejectOTByTecnicoData & {
  estado_imagen_entrada?: string;
  estado_imagen_salida?: string;
};

export const rejectOTByTecnico = async ({
  data,
  id,
}: UpdOTRejectByTecnicoParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put<OrdenTrabajoOld>(
    `${urlBackend}api/v1/ordenes-trabajo/${id}/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const updOTSetSolicitudTransferenciaSend = async (id: string) => {
  return put<OrdenTrabajoOld>(
    `${urlBackend}api/v1/ordenes-trabajo/${id}/`,
    { solicitud_transferencia_enviada: true },
    true
  );
};

export const updOTSolicitarCambioPuertoNap = async (id: string) => {
  return put<OrdenTrabajoOld>(
    `${urlBackend}api/v1/ordenes-trabajo/${id}/`,
    { estado_activacion: EstadoActivacionCodigoEnum.PENDIENTE_CAMBIO_DATOS },
    true
  );
};

export const updOTAndServicioInternet = async (data: any) => {
  return put<OrdenTrabajoOld>(
    `${urlBackend}api/v1/actualizar-servicio-orden/`,
    data,
    true
  );
};

export const useUpdateOrdenTrabajoAndServicioInternet = ({
  navigate,
  returnUrl,
  customMessage,
  enableAlert = true,
  enableRedirect = true,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updOTAndServicioInternet,
    onSuccess: res => {
      queryClient.invalidateQueries({
        queryKey: ['ordeneTrabajo', 'ordenesTrabajo'],
      });

      if (res.status === 'success') {
        enableAlert &&
          setAlert(
            true,
            customMessage ?? 'Actualizado correctamente',
            AlertSeverityEnum.success
          );
      }

      enableRedirect && navigate && returnUrl && navigate(returnUrl);
    },
  });
};
